import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { Can } from '@/components/common/Can';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RBAC_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { RAISED_CARD_CLASS } from '@/styles/raisedCard';
import { formatCount } from '@/utils/number';
import { useUserRoles } from '../hooks/useUserRoles';

interface UserAccessSectionProps {
  userId: number;
}

export function UserAccessSection({ userId }: UserAccessSectionProps) {
  const { can } = usePermissions();
  const mayManage = can(RBAC_PERMISSIONS.USER_ROLES_EDIT);
  const {
    roles,
    isLoading,
    error,
    selectedIds,
    toggle,
    isDirty,
    reset,
    save,
    isSaving,
    effectiveGroups,
    viaRoleByCode,
    isEffectiveLoading,
    effectiveError,
  } = useUserRoles(userId);

  return (
    <Card className={RAISED_CARD_CLASS}>
      <CardContent className="flex flex-col gap-5">
        <div className="flex items-center gap-2.5">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ShieldCheck className="size-4" aria-hidden />
          </div>
          <h3 className="text-base font-semibold">Access</h3>
        </div>

        <section className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Permissions come from the roles a user holds, and from anything given to this user
            alone. A user may hold more than one role.
          </p>

          {error ? (
            <ErrorState error={error} fallbackTitle="Could not load the roles" />
          ) : isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : roles.length === 0 ? (
            <EmptyState kind="noData" title="No role has been created yet" />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-start gap-2 rounded-md px-1 py-1 text-sm hover:bg-muted/50"
                >
                  <Checkbox
                    checked={selectedIds.has(role.id)}
                    onCheckedChange={() => toggle(role.id)}
                    disabled={!mayManage || isSaving}
                  />
                  <span className="min-w-0">
                    <span className="block truncate">
                      {role.name}
                      {role.isActive ? null : (
                        <span className="ml-1 text-xs text-muted-foreground">(inactive)</span>
                      )}
                    </span>
                    <span className="block truncate font-mono text-xs text-muted-foreground">
                      {role.code}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <Can code={RBAC_PERMISSIONS.USER_ROLES_EDIT}>
            <div className="flex items-center gap-2">
              <Button type="button" onClick={save} disabled={!isDirty || isSaving} size="sm">
                {isSaving ? 'Saving' : 'Save roles'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={reset}
                disabled={!isDirty || isSaving}
              >
                Undo
              </Button>
            </div>
          </Can>
        </section>

        <section className="flex flex-col gap-3 border-t pt-4">
          <div className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted-foreground" aria-hidden />
            <h4 className="text-sm font-semibold">What this gives them</h4>
          </div>

          {effectiveError ? (
            <ErrorState error={effectiveError} fallbackTitle="Could not load the permissions" />
          ) : isEffectiveLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : effectiveGroups.length === 0 ? (
            <EmptyState
              kind="noData"
              title="No permission yet"
              description="Give the user a role that holds some, add permissions to a role they already hold, or give this user some of their own."
            />
          ) : (
            <div className="flex flex-col gap-4">
              {effectiveGroups.map((group) => (
                <div key={group.module} className="flex flex-col gap-1">
                  <p className="border-b pb-1 text-sm font-semibold">{group.module}</p>
                  <ul className="grid gap-1 sm:grid-cols-2">
                    {group.permissions.map((permission) => (
                      <li key={permission.code} className="text-sm">
                        <span className="block truncate">{permission.description}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          via {(viaRoleByCode.get(permission.code) ?? []).join(', ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {formatCount(
              effectiveGroups.reduce((total, group) => total + group.permissions.length, 0),
              'permission',
            )}{' '}
            in total. An inactive role, or a permission no endpoint declares any more, is left out.
          </p>
        </section>
      </CardContent>
    </Card>
  );
}
